import React, { useEffect, useRef, useState, useCallback } from 'react';
import jsQR from 'jsqr';
import { Loader2, Camera, X, CheckCircle } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { Button } from '@/components/ui/button';

export type ScanState = 'idle' | 'requesting' | 'scanning' | 'decoded' | 'error';

export interface QrScannerResult {
  value: string;
  type: 'npub' | 'nprofile' | 'unknown';
  identifier: string;
}

export function useQrScanner() {
  const [scanState, setScanState] = useState<ScanState>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [result, setResult] = useState<QrScannerResult | null>(null);

  const reset = useCallback(() => {
    setScanState('idle');
    setErrorMsg('');
    setResult(null);
  }, []);

  return { scanState, setScanState, errorMsg, setErrorMsg, result, setResult, reset };
}

/**
 * Parse a decoded string into a QrScannerResult
 */
export function parseScanResult(decoded: string): QrScannerResult {
  // Handle nostr:npub1... (NIP-21 URI)
  let value = decoded;
  if (value.startsWith('nostr:')) {
    value = value.slice(6);
  }

  try {
    const { type, data } = nip19.decode(value);
    if (type === 'npub' || type === 'nprofile') {
      const identifier = type === 'npub' ? value : decoded;
      return { value, type, identifier };
    }
  } catch {
    // Not a valid NIP-19 identifier
  }

  return { value: decoded, type: 'unknown', identifier: decoded };
}

interface QrScannerViewProps {
  onResult: (result: QrScannerResult) => void;
  onError: (error: string) => void;
  onBack: () => void;
  scanState: ScanState;
  setScanState: (state: ScanState) => void;
}

/**
 * QrScanner - Camera-based QR code scanner using jsQR
 * Displays the camera feed and decodes QR codes in real-time
 */
export function QrScannerView({
  onResult,
  onError,
  onBack,
  scanState,
  setScanState,
}: QrScannerViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animRef = useRef<number>(0);

  // Use refs for callbacks to prevent infinite effect loops when parent passes inline functions
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const onBackRef = useRef(onBack);
  onResultRef.current = onResult;
  onErrorRef.current = onError;
  onBackRef.current = onBack;

  const startCamera = useCallback(async () => {
    setScanState('requesting');

    try {
      // Request rear camera first, fall back to any camera
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } },
        });
      } catch {
        // Fallback to any camera if rear camera isn't available
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.play();
        setScanState('scanning');
      }
    } catch (err: any) {
      const errFn = onErrorRef.current;
      if (err.name === 'NotAllowedError') {
        errFn('Camera permission denied. Please enable camera access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        errFn('No camera found on this device.');
      } else if (err.name === 'NotSupportedError') {
        errFn('Your browser does not support camera access.');
      } else {
        errFn('Could not access camera: ' + (err.message || 'unknown error'));
      }
    }
  }, [setScanState]); // onError removed from deps since we use the ref

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    cancelAnimationFrame(animRef.current);
  }, []);

  // Start camera on mount
  useEffect(() => {
    startCamera();

    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  // Decode loop
  useEffect(() => {
    if (scanState !== 'scanning') return;

    const decode = () => {
      if (!videoRef.current || !canvasRef.current) {
        animRef.current = requestAnimationFrame(decode);
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (video.readyState < 2) {
        animRef.current = requestAnimationFrame(decode);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        animRef.current = requestAnimationFrame(decode);
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code) {
        const result = parseScanResult(code.data);
        if (result.type === 'npub' || result.type === 'nprofile') {
          setScanState('decoded');
          // Vibrate on mobile if supported
          if (navigator.vibrate) navigator.vibrate(200);
          // Hold the result frame for a moment before proceeding
          setTimeout(() => {
            stopCamera();
            onResultRef.current(result);
          }, 800);
          return;
        }
      }

      // Continue scanning
      animRef.current = requestAnimationFrame(decode);
    };

    animRef.current = requestAnimationFrame(decode);

    return () => cancelAnimationFrame(animRef.current);
  }, [scanState, stopCamera, setScanState]); // onResult removed from deps since we use the ref

  return (
    <div className="relative w-full min-h-[260px] rounded-2xl overflow-hidden bg-black">
      {/* Camera feed */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        muted
        playsInline
      />

      {/* Hidden canvas for frame analysis */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Scanning overlay frame */}
      {scanState === 'scanning' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-48 h-48">
            {/* Corner brackets */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-sky-400 rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-sky-400 rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-sky-400 rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-sky-400 rounded-br-lg" />
            {/* Scanning line animation */}
            <div className="absolute left-1 right-1 h-0.5 bg-sky-400/80 animate-scan-line shadow-lg shadow-sky-400/50" />
          </div>
        </div>
      )}

      {/* Requesting loading state */}
      {scanState === 'requesting' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white gap-2">
          <Loader2 className="size-8 animate-spin" />
          <p className="text-xs font-medium">Requesting camera access...</p>
        </div>
      )}

      {/* Error state */}
      {scanState === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white gap-3 p-4 text-center">
          <X className="size-8 text-rose-400" />
          <p className="text-xs leading-normal">Camera not available.<br/>Try pasting an npub manually.</p>
        </div>
      )}

      {/* Decoded state - freeze frame with success */}
      {scanState === 'decoded' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 gap-2 animate-fade-in">
          <CheckCircle className="size-12 text-teal-500 animate-scale-in" />
          <p className="text-sm font-bold text-foreground">QR Code Detected!</p>
          <p className="text-xs text-muted-foreground">Opening profile...</p>
        </div>
      )}

      {/* Bottom bar with cancel button */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
        <Button
          type="button"
          onClick={() => {
            stopCamera();
            onBackRef.current();
          }}
          variant="outline"
          className="w-full h-9 rounded-full bg-white/20 hover:bg-white/30 border-white/30 text-white font-bold text-xs backdrop-blur-sm cursor-pointer"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default QrScannerView;